import React, { useEffect, useState } from 'react';
import { useStore } from '../store/useStore';
import { Avatar } from '../components/UI';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area, CartesianGrid } from 'recharts';
import { TrendingUp, TrendingDown, Clock, CheckCircle2, Ticket, MessageSquare } from 'lucide-react';
import { api } from '../api';

type Period = 'today' | 'week' | 'month' | 'all';

const periodConfig: Record<Period, { label: string; days: number }> = {
  today: { label: 'Сегодня', days: 1 },
  week: { label: 'Неделя', days: 7 },
  month: { label: 'Месяц', days: 30 },
  all: { label: 'Всё время', days: 365 },
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div className="bg-white/95 backdrop-blur-xl border border-black/8 rounded-[12px] shadow-lg px-3 py-2">
        <p className="text-[11px] text-[#8E8E93] mb-1">{label}</p>
        <p className="text-[15px] font-semibold text-black">{payload[0].value} {payload[0].name === 'tickets' ? 'тик.' : 'мин.'}</p>
      </div>
    );
  }
  return null;
};

export const StatsScreen = () => {
  const { operators } = useStore();
  const [period, setPeriod] = useState<Period>('month');
  const [stats, setStats] = useState({
    totalTickets: 0,
    totalTicketsTrend: 0,
    openTickets: 0,
    avgResponseTimeMin: 0,
    closedPeriod: 0,
    chartByDay: [] as { date: string; tickets: number }[],
    chartResponseTime: [] as { date: string; timeMin: number }[],
    operatorPerformance: [] as { operatorId: string; tickets: number; closed: number; avgTimeMin: number }[],
  });
  const roleLabel = (role?: (typeof operators)[number]['role']) =>
    role === 'OWNER' ? 'Owner' : role === 'ADMIN' ? 'Администратор' : role === 'VIEWER' ? 'Наблюдатель' : 'Оператор';

  useEffect(() => {
    void api.getStats(period).then(setStats).catch(() => {
      setStats({
        totalTickets: 0,
        totalTicketsTrend: 0,
        openTickets: 0,
        avgResponseTimeMin: 0,
        closedPeriod: 0,
        chartByDay: [],
        chartResponseTime: [],
        operatorPerformance: [],
      });
    });
  }, [period]);

  return (
    <div className="flex-1 h-full bg-[#F5F5F7] flex flex-col mobile-content-safe overflow-y-auto">
      <div className="p-4 md:p-8 max-w-5xl w-full mx-auto space-y-6">
        {/* Title + Period selector */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-4 mt-2">
          <h2 className="text-[34px] font-bold text-black tracking-tight">Статистика</h2>
          <div className="bg-black/[0.06] p-[3px] rounded-lg flex text-[13px] font-medium w-full sm:w-auto shadow-inner">
            {(Object.keys(periodConfig) as Period[]).map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`flex-1 sm:w-24 py-1.5 px-2 rounded-md transition-all text-center ${
                  period === p
                    ? 'bg-white shadow-[0_1px_3px_rgba(0,0,0,0.12),0_1px_2px_rgba(0,0,0,0.06)] text-black'
                    : 'text-black/60 hover:text-black/80'
                }`}
              >
                {periodConfig[p].label}
              </button>
            ))}
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <StatCard
            title="Всего тикетов"
            value={stats.totalTickets.toLocaleString('ru-RU')}
            icon={<Ticket size={20} strokeWidth={2} />}
            trend={Math.abs(stats.totalTicketsTrend)}
            trendUp={stats.totalTicketsTrend >= 0}
            color="text-[#007AFF]"
            bg="bg-[#007AFF]/8"
          />
          <StatCard
            title="Открытые"
            value={stats.openTickets}
            icon={<Clock size={20} strokeWidth={2} />}
            color="text-[#FF9500]"
            bg="bg-[#FF9500]/8"
          />
          <StatCard
            title="Ср. время ответа"
            value={`${stats.avgResponseTimeMin} мин`}
            icon={<MessageSquare size={20} strokeWidth={2} />}
            color="text-[#5856D6]"
            bg="bg-[#5856D6]/8"
          />
          <StatCard
            title="Закрыто"
            value={stats.closedPeriod.toLocaleString('ru-RU')}
            icon={<CheckCircle2 size={20} strokeWidth={2} />}
            color="text-[#34C759]"
            bg="bg-[#34C759]/8"
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          <div className="bg-white p-5 rounded-[20px] shadow-sm border border-black/5">
            <div className="flex justify-between items-start mb-5">
              <div>
                <h3 className="text-[16px] font-semibold text-black tracking-tight">Тикеты по дням</h3>
                <p className="text-[13px] text-[#8E8E93] mt-0.5">{periodConfig[period].label}</p>
              </div>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.chartByDay} margin={{ top: 4, right: 4, left: -20, bottom: 0 }} barCategoryGap="30%">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" vertical={false} />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#8E8E93', fontSize: 11 }} dy={8} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#8E8E93', fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(0,0,0,0.03)', radius: 6 }} />
                  <Bar dataKey="tickets" fill="#007AFF" radius={[6, 6, 2, 2]} maxBarSize={40} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white p-5 rounded-[20px] shadow-sm border border-black/5">
            <div className="flex justify-between items-start mb-5">
              <div>
                <h3 className="text-[16px] font-semibold text-black tracking-tight">Время ответа</h3>
                <p className="text-[13px] text-[#8E8E93] mt-0.5">Среднее, в минутах</p>
              </div>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats.chartResponseTime} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorTime" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#34C759" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="#34C759" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.04)" vertical={false} />
                  <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#8E8E93', fontSize: 11 }} dy={8} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fill: '#8E8E93', fontSize: 11 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="timeMin" stroke="#34C759" strokeWidth={2.5} fillOpacity={1} fill="url(#colorTime)" dot={false} activeDot={{ r: 5, fill: '#34C759' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Operator Performance */}
        <div className="bg-white rounded-[20px] shadow-sm border border-black/5 overflow-hidden">
          <div className="px-6 py-4 border-b border-black/5">
            <h3 className="text-[16px] font-semibold text-black tracking-tight">Эффективность операторов</h3>
            <p className="text-[13px] text-[#8E8E93] mt-0.5">{periodConfig[period].label}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-black/5 bg-[#F9F9F9]">
                  <th className="px-6 py-3 text-[11px] font-bold text-[#8E8E93] uppercase tracking-widest">Оператор</th>
                  <th className="px-6 py-3 text-[11px] font-bold text-[#8E8E93] uppercase tracking-widest text-right">Тикеты</th>
                  <th className="px-6 py-3 text-[11px] font-bold text-[#8E8E93] uppercase tracking-widest text-right">Закрыто</th>
                  <th className="px-6 py-3 text-[11px] font-bold text-[#8E8E93] uppercase tracking-widest text-right">Ср. ответ</th>
                  <th className="px-6 py-3 text-[11px] font-bold text-[#8E8E93] uppercase tracking-widest text-right">%</th>
                </tr>
              </thead>
              <tbody>
                {stats.operatorPerformance.map((perf, idx) => {
                  const op = operators.find(o => o.id === perf.operatorId);
                  if (!op) return null;
                  const pct = perf.tickets > 0 ? Math.round((perf.closed / perf.tickets) * 100) : 0;
                  return (
                    <tr
                      key={op.id}
                      className={`hover:bg-[#F9F9F9] transition-colors cursor-pointer ${idx !== stats.operatorPerformance.length - 1 ? 'border-b border-black/5' : ''}`}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <Avatar name={op.name} size="34px" isOnline={op.isOnline} />
                          <div>
                            <div className="font-medium text-[14px] text-black">{op.name}</div>
                            <div className="text-[12px] text-[#8E8E93]">{roleLabel(op.role)}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-[15px] text-black font-medium text-right">{perf.tickets.toLocaleString('ru-RU')}</td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-[15px] text-[#34C759] font-semibold">{perf.closed.toLocaleString('ru-RU')}</span>
                      </td>
                      <td className="px-6 py-4 text-[15px] text-black font-medium text-right">{perf.avgTimeMin} мин</td>
                      <td className="px-6 py-4 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <div className="w-16 h-1.5 bg-[#F2F2F7] rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full bg-[#34C759] transition-all duration-500"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="text-[13px] font-medium text-[#34C759] w-9 text-right">{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, color = 'text-black', bg = 'bg-black/5', trend, trendUp }: {
  title: string; value: string | number; icon: React.ReactNode;
  color?: string; bg?: string; trend?: number; trendUp?: boolean;
}) => (
  <div className="bg-white p-4 md:p-5 rounded-[20px] shadow-sm border border-black/5 flex flex-col justify-between hover:shadow-md transition-shadow">
    <div className="flex justify-between items-start mb-3">
      <div className={`w-10 h-10 rounded-full ${bg} flex items-center justify-center ${color}`}>
        {icon}
      </div>
      {trend !== undefined && trendUp !== undefined && (
        <span className={`text-[12px] font-bold flex items-center gap-1 px-2 py-0.5 rounded-full ${trendUp ? 'text-[#34C759] bg-[#34C759]/10' : 'text-[#FF3B30] bg-[#FF3B30]/10'}`}>
          {trendUp ? <TrendingUp size={12} strokeWidth={2.5} /> : <TrendingDown size={12} strokeWidth={2.5} />}
          {trendUp ? '+' : '-'}{trend}%
        </span>
      )}
    </div>
    <div>
      <div className="text-[28px] md:text-[32px] font-bold tracking-tight leading-none mb-1 text-black">{value}</div>
      <div className="text-[13px] font-medium text-[#8E8E93]">{title}</div>
    </div>
  </div>
);
